// JMAP client. Talks to the Worker proxy at /api/jmap; the Worker handles
// the Stalwart Basic-auth + per-user secret lookup. The browser never sees a
// Stalwart credential and there's no CORS/token-rotation dance.
//
// Spec: https://jmap.io/spec-core.html / https://jmap.io/spec-mail.html
// All JMAP wire types are camelCase; we marshal to our snake_case Layer7
// interfaces (Mailbox, Email, EmailThread) at the edges.

import type {
  Email, EmailAddress, EmailAttachment, EmailThread, Mailbox, MailAccount, AccountId,
} from "@/types";

interface JmapSession {
  accountId: AccountId;
  primaryAccountId: AccountId;
  email: string;
  capabilities: Record<string, unknown>;
  accounts: Record<string, MailAccount>;
}

interface RawJmapSession {
  capabilities?: Record<string, unknown>;
  accounts?: Record<string, { name?: string; isPersonal?: boolean }>;
  primaryAccounts?: Record<string, string>;
}

interface RawMailbox {
  id: string;
  name: string;
  parentId: string | null;
  role: string | null;
  totalEmails: number;
  unreadEmails: number;
  totalThreads: number;
  unreadThreads: number;
  sortOrder?: number;
}

interface RawBodyPart {
  partId: string;
  type?: string;
}

interface RawEmail {
  id: string;
  threadId: string;
  mailboxIds: Record<string, boolean>;
  keywords?: Record<string, boolean>;
  from?: Array<{ name?: string | null; email: string }>;
  to?: Array<{ name?: string | null; email: string }>;
  cc?: Array<{ name?: string | null; email: string }>;
  bcc?: Array<{ name?: string | null; email: string }>;
  replyTo?: Array<{ name?: string | null; email: string }>;
  subject?: string;
  receivedAt?: string;
  sentAt?: string;
  preview?: string;
  hasAttachment?: boolean;
  bodyValues?: Record<string, { value: string; isTruncated?: boolean }>;
  textBody?: RawBodyPart[];
  htmlBody?: RawBodyPart[];
  attachments?: Array<{
    blobId: string;
    name?: string | null;
    type?: string;
    size?: number;
    cid?: string | null;
  }>;
  messageId?: string[];
  inReplyTo?: string[];
  references?: string[];
  // Custom header we set when sending — joins to outbound_mail_events for open/click events.
  "header:X-Layer7-Tracking-Id:asText"?: string | null;
}

const ROLE_MAP: Record<string, Mailbox["role"]> = {
  inbox: "inbox",
  archive: "archive",
  drafts: "drafts",
  sent: "sent",
  trash: "trash",
  junk: "spam",
  spam: "spam",
  scheduled: "scheduled",
};

function mapMailbox(r: RawMailbox): Mailbox {
  return {
    id: r.id,
    name: r.name,
    parent_id: r.parentId,
    role: r.role ? ROLE_MAP[r.role.toLowerCase()] ?? null : null,
    total_emails: r.totalEmails,
    unread_emails: r.unreadEmails,
    total_threads: r.totalThreads,
    unread_threads: r.unreadThreads,
    sort_order: r.sortOrder ?? 0,
  };
}

function mapAddrs(arr?: Array<{ name?: string | null; email: string }>): EmailAddress[] {
  if (!arr) return [];
  return arr.map((a) => ({ name: a.name ?? undefined, email: a.email }));
}

function mapEmail(r: RawEmail): Email {
  // RFC 8621 4.1.4: with no alternative, htmlBody lists the text/plain part
  // (and textBody the text/html one), so take each by its type. Rendering
  // plain text as HTML collapsed its line breaks and ran "> " quotes together.
  const bodyOf = (parts: RawBodyPart[] | undefined, type: string) => {
    const part = parts?.find((p) => (p.type ?? type) === type);
    return part ? r.bodyValues?.[part.partId]?.value : undefined;
  };
  const bodyText = bodyOf(r.textBody, "text/plain");
  const bodyHtml = bodyOf(r.htmlBody, "text/html");
  return {
    id: r.id,
    thread_id: r.threadId,
    mailbox_ids: Object.keys(r.mailboxIds ?? {}).filter((k) => r.mailboxIds[k]),
    keywords: r.keywords ?? {},
    from: mapAddrs(r.from),
    to: mapAddrs(r.to),
    cc: r.cc ? mapAddrs(r.cc) : undefined,
    bcc: r.bcc ? mapAddrs(r.bcc) : undefined,
    reply_to: r.replyTo ? mapAddrs(r.replyTo) : undefined,
    subject: r.subject ?? "",
    received_at: r.receivedAt ?? new Date().toISOString(),
    sent_at: r.sentAt,
    preview: r.preview ?? "",
    has_attachment: r.hasAttachment ?? false,
    body_text: bodyText,
    body_html: bodyHtml,
    attachments: r.attachments?.map<EmailAttachment>((a) => ({
      blob_id: a.blobId,
      name: a.name ?? "attachment",
      type: a.type ?? "application/octet-stream",
      size: a.size ?? 0,
      cid: a.cid ?? undefined,
    })),
    rfc822_message_id: r.messageId?.[0] ?? r.id,
    in_reply_to: r.inReplyTo?.[0],
    tracking_id: r["header:X-Layer7-Tracking-Id:asText"]?.trim() || undefined,
  };
}

// Raw POST to /api/jmap. Returns the methodResponses array.
type MethodCall = [method: string, args: unknown, callId: string];

async function jmapBatch<T = unknown>(calls: MethodCall[]): Promise<T[]> {
  const res = await fetch("/api/jmap", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", "Origin": window.location.origin },
    body: JSON.stringify({
      using: [
        "urn:ietf:params:jmap:core",
        "urn:ietf:params:jmap:mail",
        "urn:ietf:params:jmap:submission",
      ],
      methodCalls: calls,
    }),
  });
  if (!res.ok) throw new Error(`JMAP batch failed: ${res.status}`);
  const body = (await res.json()) as { methodResponses?: Array<[string, unknown, string]> };
  if (!body.methodResponses) throw new Error("JMAP response missing methodResponses");
  // Surface method-level errors instead of returning them as silent success.
  // Stalwart returns ["error", {type, description}, callId] in place of the
  // expected method name when a single call fails — without this check the
  // caller would cast {type, description} as the success type and downstream
  // `(resp.list ?? []).map(...)` would render an empty list with no error.
  return body.methodResponses.map((r, idx) => {
    if (r[0] === "error") {
      const e = r[1] as { type?: string; description?: string };
      const call = calls[idx]?.[0] ?? "?";
      throw new Error(`JMAP ${call} error: ${e.type ?? "unknown"}${e.description ? ` — ${e.description}` : ""}`);
    }
    return r[1] as T;
  });
}

let sessionCache: JmapSession | null = null;

interface JmapApiShape {
  session(): Promise<JmapSession>;
  clearSession(): void;
  sendAs(body: {
    from: string;
    to: { email: string; name?: string }[];
    cc?: { email: string; name?: string }[];
    bcc?: { email: string; name?: string }[];
    subject: string;
    text?: string;
    html?: string;
    in_reply_to?: string;
    references?: string[];
    attachments?: Array<{ blobId: string; name: string; type: string; size?: number }>;
  }): Promise<{ emailId: string; submissionId: string; trackingId: string; from: string }>;
  uploadBlob(file: File): Promise<{ blobId: string; type: string; size: number }>;
  listMailboxes(accountId: AccountId): Promise<{ list: Mailbox[] }>;
  queryEmails(
    accountId: AccountId,
    opts: {
      mailboxId?: string;
      filter?: Record<string, unknown>;
      sort?: Array<{ property: string; isAscending?: boolean }>;
      position?: number;
      limit?: number;
      collapseThreads?: boolean;
    },
  ): Promise<{ ids: string[]; total: number; position: number }>;
  getEmails(accountId: AccountId, ids: string[], properties?: string[]): Promise<{ list: Email[] }>;
  /** Fetch the full RFC 5322 header set for an email, ordered as in the
   *  original message. Used by the "View headers" affordance in the reader. */
  getEmailHeaders(accountId: AccountId, id: string): Promise<Array<{ name: string; value: string }>>;
  queryAndGet(
    accountId: AccountId,
    opts: Parameters<JmapApiShape["queryEmails"]>[1],
  ): Promise<{ emails: Email[]; total: number; nextPosition: number }>;
  getThreads(accountId: AccountId, ids: string[]): Promise<{ list: EmailThread[] }>;
  getThreadWithEmails(accountId: AccountId, threadId: string): Promise<Email[]>;
  setKeywords(accountId: AccountId, id: string, keywords: Record<string, boolean>): Promise<void>;
  /** Mark every unread message in a mailbox as read ($seen). Returns the number
   *  marked. Queries unread message ids (paginated) then bulk Email/set them. */
  markMailboxRead(accountId: AccountId, mailboxId: string): Promise<number>;
  move(accountId: AccountId, id: string, mailboxIds: string[]): Promise<void>;
  delete(accountId: AccountId, ids: string[]): Promise<void>;
  /** Resolve the account's Drafts mailbox id, creating it if missing. */
  ensureDraftsMailbox(accountId: AccountId): Promise<string>;
  /** Persist a composer draft. JMAP Emails are immutable, so "editing" a draft
   *  creates a NEW Email and destroys the prior one (d.draftId) in one set.
   *  Returns the new Email's id + threadId. */
  saveDraft(
    accountId: AccountId,
    d: {
      draftId?: string;
      draftsMailboxId: string;
      from: { email: string; name?: string };
      to: { email: string; name?: string }[];
      cc?: { email: string; name?: string }[];
      bcc?: { email: string; name?: string }[];
      subject: string;
      text?: string;
      html?: string;
      inReplyTo?: string;
      references?: string[];
    },
  ): Promise<{ id: string; threadId: string }>;
  deleteDraft(accountId: AccountId, id: string): Promise<void>;
  send(
    accountId: AccountId,
    body: {
      from: { email: string; name?: string };
      to: { email: string; name?: string }[];
      cc?: { email: string; name?: string }[];
      bcc?: { email: string; name?: string }[];
      subject: string;
      html?: string;
      text?: string;
      in_reply_to?: string;
      references?: string[];
      identityId?: string;
    },
  ): Promise<{ emailId: string; submissionId: string }>;
  pushUrl(account: AccountId, types?: string): string;
  blobUrl(blobId: string, name?: string, type?: string, emailId?: string): string;
}

export const JmapApi: JmapApiShape = {
  async session(): Promise<JmapSession> {
    if (sessionCache) return sessionCache;
    const res = await fetch("/api/jmap/session", {
      credentials: "same-origin",
      headers: { "Origin": window.location.origin },
    });
    if (!res.ok) throw new Error(`JMAP session fetch failed: ${res.status}`);
    const raw = (await res.json()) as RawJmapSession;
    const primaryId =
      raw.primaryAccounts?.["urn:ietf:params:jmap:mail"] ??
      Object.keys(raw.accounts ?? {})[0] ??
      "";
    if (!primaryId) throw new Error("JMAP session has no accounts");
    const primary = raw.accounts?.[primaryId];
    sessionCache = {
      accountId: primaryId,
      primaryAccountId: primaryId,
      email: primary?.name ?? "",
      capabilities: raw.capabilities ?? {},
      accounts: Object.fromEntries(
        Object.entries(raw.accounts ?? {}).map(([id, a]) => [
          id,
          {
            id,
            email: a.name ?? "",
            name: a.name ?? "",
            is_primary: id === primaryId,
            unread_total: 0,
          } satisfies MailAccount,
        ])
      ),
    };
    return sessionCache;
  },

  clearSession(): void {
    sessionCache = null;
  },

  async listMailboxes(accountId: AccountId): Promise<{ list: Mailbox[] }> {
    const [resp] = await jmapBatch<{ list: RawMailbox[] }>([
      ["Mailbox/get", { accountId, ids: null }, "0"],
    ]);
    return { list: (resp.list ?? []).map(mapMailbox) };
  },

  async queryEmails(
    accountId: AccountId,
    opts: {
      mailboxId?: string;
      filter?: Record<string, unknown>;
      sort?: Array<{ property: string; isAscending?: boolean }>;
      position?: number;
      limit?: number;
      collapseThreads?: boolean;
    },
  ): Promise<{ ids: string[]; total: number; position: number }> {
    const filter = opts.mailboxId ? { inMailbox: opts.mailboxId, ...(opts.filter ?? {}) } : opts.filter;
    const [resp] = await jmapBatch<{ ids: string[]; total: number; position: number }>([
      [
        "Email/query",
        {
          accountId,
          filter,
          sort: opts.sort ?? [{ property: "receivedAt", isAscending: false }],
          position: opts.position ?? 0,
          limit: opts.limit ?? 40,
          collapseThreads: opts.collapseThreads ?? true,
          calculateTotal: true,
        },
        "0",
      ],
    ]);
    return resp;
  },

  async getEmails(
    accountId: AccountId,
    ids: string[],
    properties?: string[],
  ): Promise<{ list: Email[] }> {
    if (ids.length === 0) return { list: [] };
    const defaultProps = [
      "id", "threadId", "mailboxIds", "keywords",
      "from", "to", "cc", "bcc", "replyTo",
      "subject", "receivedAt", "sentAt", "preview", "hasAttachment",
      "textBody", "htmlBody", "bodyValues",
      "attachments", "messageId", "inReplyTo", "references",
      // Custom tracking id header. JMAP returns it under the same property name.
      "header:X-Layer7-Tracking-Id:asText",
    ];
    const [resp] = await jmapBatch<{ list: RawEmail[] }>([
      [
        "Email/get",
        {
          accountId,
          ids,
          properties: properties ?? defaultProps,
          fetchAllBodyValues: true,
          maxBodyValueBytes: 200_000,
        },
        "0",
      ],
    ]);
    return { list: (resp.list ?? []).map(mapEmail) };
  },

  async queryAndGet(
    accountId: AccountId,
    opts: Parameters<typeof JmapApi.queryEmails>[1],
  ): Promise<{ emails: Email[]; total: number; nextPosition: number }> {
    // Single round-trip via JMAP back-references: Email/get uses #ids
    // pointing at Email/query's result. Cuts inbox load latency by ~half
    // (was 2 sequential round trips through our CF Worker + cloudflared
    // tunnel + Stalwart, now 1).
    //
    // Also slims the property list to LIST-VIEW essentials — no bodyValues,
    // no full HTML, no attachments. Each of those is potentially hundreds of
    // KB per message; for an inbox of 40 emails this was downloading multi-MB
    // of body content the user never sees in the list view. Full bodies get
    // fetched lazily via getEmails when the user opens a specific message.
    const filter = opts.mailboxId ? { inMailbox: opts.mailboxId, ...(opts.filter ?? {}) } : opts.filter;
    const listProps = [
      "id", "threadId", "mailboxIds", "keywords",
      "from", "to", "cc", "subject", "receivedAt", "sentAt",
      "preview", "hasAttachment", "size",
      "messageId", "inReplyTo", "references",
      "header:X-Layer7-Tracking-Id:asText",
    ];
    const [queryResp, getResp] = await jmapBatch<
      { ids: string[]; total: number; position: number } | { list: RawEmail[] }
    >([
      ["Email/query", {
        accountId,
        filter,
        sort: opts.sort ?? [{ property: "receivedAt", isAscending: false }],
        position: opts.position ?? 0,
        limit: opts.limit ?? 40,
        collapseThreads: opts.collapseThreads ?? true,
        calculateTotal: true,
      }, "0"],
      ["Email/get", {
        accountId,
        "#ids": { resultOf: "0", name: "Email/query", path: "/ids" },
        properties: listProps,
        // fetchAllBodyValues OMITTED — list view doesn't need bodies; lazy
        // fetch happens in getEmails when user opens a message.
      }, "1"],
    ]);
    const q = queryResp as { ids: string[]; total: number; position: number };
    const g = getResp  as { list: RawEmail[] };
    return {
      emails: (g.list ?? []).map(mapEmail),
      total: q.total,
      nextPosition: q.position + q.ids.length,
    };
  },

  // Returns ALL RFC 5322 headers for the given email, preserving multiplicity
  // (Received: lines, multiple Authentication-Results:) and original order.
  // Implemented via JMAP Email/get with the `headers` property, which returns
  // a [{name, value}] array per the spec.
  async getEmailHeaders(accountId: AccountId, id: string): Promise<Array<{ name: string; value: string }>> {
    const [resp] = await jmapBatch<{
      list: Array<{ id: string; headers?: Array<{ name: string; value: string }> }>;
    }>([
      ["Email/get", { accountId, ids: [id], properties: ["id", "headers"] }, "0"],
    ]);
    const found = resp.list?.[0];
    return found?.headers ?? [];
  },

  async getThreads(accountId: AccountId, ids: string[]): Promise<{ list: EmailThread[] }> {
    if (ids.length === 0) return { list: [] };
    const [resp] = await jmapBatch<{ list: Array<{ id: string; emailIds: string[] }> }>([
      ["Thread/get", { accountId, ids }, "0"],
    ]);
    return {
      list: (resp.list ?? []).map((t) => ({
        id: t.id,
        email_ids: t.emailIds,
        unread_count: 0,
        has_attachment: false,
        preview: "",
      })),
    };
  },

  /** Single round-trip: Thread/get → Email/get via back-references. Returns
   *  the full Email[] for the thread (oldest → newest sort is the caller's
   *  job). Used by the mail reader to open a conversation in one network
   *  request instead of two sequential. */
  async getThreadWithEmails(accountId: AccountId, threadId: string): Promise<Email[]> {
    const fullProps = [
      "id", "threadId", "mailboxIds", "keywords",
      "from", "to", "cc", "bcc", "replyTo",
      "subject", "receivedAt", "sentAt", "preview", "hasAttachment",
      "textBody", "htmlBody", "bodyValues",
      "attachments", "messageId", "inReplyTo", "references",
      "header:X-Layer7-Tracking-Id:asText",
    ];
    const [, getResp] = await jmapBatch<
      | { list: Array<{ id: string; emailIds: string[] }> }
      | { list: RawEmail[] }
    >([
      ["Thread/get", { accountId, ids: [threadId] }, "0"],
      ["Email/get", {
        accountId,
        "#ids": { resultOf: "0", name: "Thread/get", path: "/list/*/emailIds" },
        properties: fullProps,
        fetchAllBodyValues: true,
        maxBodyValueBytes: 200_000,
      }, "1"],
    ]);
    const g = getResp as { list: RawEmail[] };
    return (g.list ?? []).map(mapEmail);
  },

  async setKeywords(
    accountId: AccountId,
    id: string,
    keywords: Record<string, boolean>,
  ): Promise<void> {
    const updates: Record<string, boolean> = {};
    for (const [k, v] of Object.entries(keywords)) updates[`keywords/${k}`] = v;
    await jmapBatch([
      ["Email/set", { accountId, update: { [id]: updates } }, "0"],
    ]);
  },

  async markMailboxRead(accountId: AccountId, mailboxId: string): Promise<number> {
    // 1) Gather ALL unread message ids in the mailbox (individual messages, not
    //    collapsed threads), paginating until we've seen `total`. 10k-message
    //    cap as a runaway guard — far beyond any real inbox's unread count.
    const ids: string[] = [];
    const pageLimit = 250;
    let position = 0;
    for (let guard = 0; guard < 40; guard++) {
      const [resp] = await jmapBatch<{ ids: string[]; total: number; position: number }>([
        ["Email/query", {
          accountId,
          filter: { inMailbox: mailboxId, notKeyword: "$seen" },
          sort: [{ property: "receivedAt", isAscending: false }],
          position,
          limit: pageLimit,
          collapseThreads: false,
          calculateTotal: true,
        }, "0"],
      ]);
      if (!resp.ids?.length) break;
      ids.push(...resp.ids);
      position += resp.ids.length;
      if (position >= resp.total) break;
    }
    if (ids.length === 0) return 0;
    // 2) Bulk-set $seen in chunks (one Email/set per chunk of 200).
    const chunkSize = 200;
    for (let i = 0; i < ids.length; i += chunkSize) {
      const update: Record<string, Record<string, boolean>> = {};
      for (const id of ids.slice(i, i + chunkSize)) update[id] = { "keywords/$seen": true };
      await jmapBatch([["Email/set", { accountId, update }, "0"]]);
    }
    return ids.length;
  },

  async move(accountId: AccountId, id: string, mailboxIds: string[]): Promise<void> {
    const mailboxMap = Object.fromEntries(mailboxIds.map((m) => [m, true]));
    await jmapBatch([
      ["Email/set", { accountId, update: { [id]: { mailboxIds: mailboxMap } } }, "0"],
    ]);
  },

  async delete(accountId: AccountId, ids: string[]): Promise<void> {
    if (ids.length === 0) return;
    await jmapBatch([["Email/set", { accountId, destroy: ids }, "0"]]);
  },

  // Find the Drafts mailbox (role=drafts, or name "Drafts"), creating it if the
  // account has none. Returns the mailbox id.
  async ensureDraftsMailbox(accountId: AccountId): Promise<string> {
    const [resp] = await jmapBatch<{ list: RawMailbox[] }>([
      ["Mailbox/get", { accountId, ids: null }, "0"],
    ]);
    const list = resp.list ?? [];
    const found = list.find(
      (m) => m.role?.toLowerCase() === "drafts" || m.name.toLowerCase() === "drafts",
    );
    if (found) return found.id;
    const [created] = await jmapBatch<{ created?: Record<string, { id: string }> }>([
      ["Mailbox/set", { accountId, create: { d: { name: "Drafts", role: "drafts" } } }, "0"],
    ]);
    const id = created.created?.d?.id;
    if (!id) throw new Error("Failed to create Drafts mailbox");
    return id;
  },

  // Persist a composer draft. Mirrors send()'s draftEmail shape but stays in the
  // Drafts mailbox (no EmailSubmission). Editing = create-new + destroy-old in
  // the same Email/set, since JMAP Emails are immutable. The new id + threadId
  // come back via a back-reference Email/get in the same batch.
  async saveDraft(
    accountId: AccountId,
    d: {
      draftId?: string;
      draftsMailboxId: string;
      from: { email: string; name?: string };
      to: { email: string; name?: string }[];
      cc?: { email: string; name?: string }[];
      bcc?: { email: string; name?: string }[];
      subject: string;
      text?: string;
      html?: string;
      inReplyTo?: string;
      references?: string[];
    },
  ): Promise<{ id: string; threadId: string }> {
    const draftEmail: Record<string, unknown> = {
      mailboxIds: { [d.draftsMailboxId]: true },
      keywords: { $draft: true, $seen: true },
      from: [d.from],
      to: d.to,
      ...(d.cc ? { cc: d.cc } : {}),
      ...(d.bcc ? { bcc: d.bcc } : {}),
      subject: d.subject,
      bodyValues: {
        ...(d.text ? { text: { value: d.text } } : {}),
        ...(d.html ? { html: { value: d.html, isHtml: true } } : {}),
      },
      ...(d.text ? { textBody: [{ partId: "text", type: "text/plain" }] } : {}),
      ...(d.html ? { htmlBody: [{ partId: "html", type: "text/html" }] } : {}),
      ...(d.inReplyTo ? { inReplyTo: [d.inReplyTo] } : {}),
      ...(d.references ? { references: d.references } : {}),
    };

    // Single Email/set. Read the created id (and server-computed threadId)
    // directly from the response — do NOT use an #ids back-ref to /created/d/id,
    // because that JSON-pointer resolves to a single Id while Email/get's `ids`
    // expects an Id[], which Stalwart rejects (failing the whole batch even
    // though the draft was created → duplicate drafts on every resave).
    const [setResp] = await jmapBatch<{
      created?: Record<string, { id: string; threadId?: string }>;
      notCreated?: Record<string, { type?: string; description?: string }>;
    }>([
      [
        "Email/set",
        {
          accountId,
          create: { d: draftEmail },
          ...(d.draftId ? { destroy: [d.draftId] } : {}),
        },
        "0",
      ],
    ]);
    const created = setResp.created?.d;
    if (!created?.id) {
      const nc = setResp.notCreated?.d;
      throw new Error(`Draft not created${nc?.description ? `: ${nc.description}` : ""}`);
    }
    // threadId is server-computed and usually returned in `created`. If the
    // server omits it, fall back to a best-effort Email/get (never fatal).
    let threadId = created.threadId ?? "";
    if (!threadId) {
      try {
        const [g] = await jmapBatch<{ list: Array<{ id: string; threadId: string }> }>([
          ["Email/get", { accountId, ids: [created.id], properties: ["id", "threadId"] }, "0"],
        ]);
        threadId = g.list?.[0]?.threadId ?? "";
      } catch { /* best-effort */ }
    }
    return { id: created.id, threadId };
  },

  async deleteDraft(accountId: AccountId, id: string): Promise<void> {
    await JmapApi.delete(accountId, [id]);
  },

  // Submission: create a Draft Email + an EmailSubmission referencing it in one batch.
  async send(
    accountId: AccountId,
    body: {
      from: { email: string; name?: string };
      to: { email: string; name?: string }[];
      cc?: { email: string; name?: string }[];
      bcc?: { email: string; name?: string }[];
      subject: string;
      html?: string;
      text?: string;
      in_reply_to?: string;
      references?: string[];
      identityId?: string;
    },
  ): Promise<{ emailId: string; submissionId: string }> {
    // Need a Drafts mailbox; resolve it via mailbox query.
    const mboxes = (await JmapApi.listMailboxes(accountId)).list;
    const drafts = mboxes.find((m) => m.role === "drafts") ?? mboxes[0];
    if (!drafts) throw new Error("No drafts mailbox found");

    const draftEmail: Record<string, unknown> = {
      mailboxIds: { [drafts.id]: true },
      keywords: { $draft: true },
      from: [body.from],
      to: body.to,
      ...(body.cc ? { cc: body.cc } : {}),
      ...(body.bcc ? { bcc: body.bcc } : {}),
      subject: body.subject,
      bodyValues: {
        ...(body.text ? { text: { value: body.text } } : {}),
        ...(body.html ? { html: { value: body.html, isHtml: true } } : {}),
      },
      ...(body.text ? { textBody: [{ partId: "text", type: "text/plain" }] } : {}),
      ...(body.html ? { htmlBody: [{ partId: "html", type: "text/html" }] } : {}),
      ...(body.in_reply_to ? { inReplyTo: [body.in_reply_to] } : {}),
      ...(body.references ? { references: body.references } : {}),
    };

    const responses = await jmapBatch<unknown>([
      ["Email/set", { accountId, create: { e: draftEmail } }, "0"],
      [
        "EmailSubmission/set",
        {
          accountId,
          ...(body.identityId ? { onSuccessUpdateEmail: { "#s": { keywords: { $sent: true, $draft: null } } } } : {}),
          create: {
            s: {
              emailId: "#e",
              envelope: {
                mailFrom: { email: body.from.email },
                rcptTo: [
                  ...body.to.map((t) => ({ email: t.email })),
                  ...(body.cc?.map((t) => ({ email: t.email })) ?? []),
                  ...(body.bcc?.map((t) => ({ email: t.email })) ?? []),
                ],
              },
            },
          },
        },
        "1",
      ],
    ]);

    const setResp = responses[0] as { created?: Record<string, { id: string }> };
    const subResp = responses[1] as { created?: Record<string, { id: string }> };
    const emailId = setResp.created?.e?.id ?? "";
    const submissionId = subResp.created?.s?.id ?? "";
    return { emailId, submissionId };
  },

  // SSE push notifications via Worker proxy.
  pushUrl(_account: AccountId, types = "Email,Mailbox,EmailDelivery"): string {
    return `/api/jmap/push?types=${encodeURIComponent(types)}&closeafter=no&ping=30`;
  },

  // Send-as: authenticates server-side as the principal owning the from-address.
  // Use this when the from-address differs from the user's primary mailbox
  // (e.g. admin sending from a shared/test mailbox). Returns the Stalwart ids.
  async sendAs(body: {
    from: string;
    to: { email: string; name?: string }[];
    cc?: { email: string; name?: string }[];
    bcc?: { email: string; name?: string }[];
    subject: string;
    text?: string;
    html?: string;
    in_reply_to?: string;
    references?: string[];
    attachments?: Array<{ blobId: string; name: string; type: string; size?: number }>;
  }): Promise<{ emailId: string; submissionId: string; trackingId: string; from: string }> {
    const res = await fetch("/api/mail/send", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", "Origin": window.location.origin },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`/api/mail/send ${res.status}: ${text.slice(0, 200)}`);
    }
    return res.json();
  },

  // Upload a single file as an attachment blob. Returns the blobId the
  // browser can then reference in the next /api/mail/send call.
  async uploadBlob(file: File): Promise<{ blobId: string; type: string; size: number }> {
    const res = await fetch("/api/jmap/blob/upload", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": file.type || "application/octet-stream", "Origin": window.location.origin },
      body: file,
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`/api/jmap/blob/upload ${res.status}: ${text.slice(0, 200)}`);
    }
    return res.json();
  },

  // Attachment download URL — Worker proxies to Stalwart /download/{accountId}/{blobId}.
  blobUrl(blobId: string, name = "attachment", type = "application/octet-stream", emailId?: string): string {
    // email_id enables the Worker's IMAP-bridge fallback: when Stalwart's
    // direct /jmap/download fails (R2 ranged-GET bug), the Worker uses
    // the email's metadata to drive an IMAP search and pulls the attachment
    // out of the raw RFC822. Without email_id the fallback can't run, so
    // attachment downloads will 500 for any S3-stored blob.
    const params = new URLSearchParams({ name, type });
    if (emailId) params.set("email_id", emailId);
    return `/api/jmap/blob/${encodeURIComponent(blobId)}?${params.toString()}`;
  },
};
